import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import Button from '../../../components/Button';
import DefaultCard from '../../../components/DefaultCard';
import GenericTable from '../../../components/GenericTable';
import IconButton from '../../../components/IconButton';
import Link from '../../../components/Link';
import ModalHeader from '../../../components/ModalHeader';
import { RadioGroup } from '../../../components/Radio';
import SelectableCard from '../../../components/SelectableCard';
import UserCard from '../../../components/UserCards/UserCard';
import UserCardGrouped from '../../../components/UserCards/UserCardGrouped';
import { EnrollmentStore } from '../../../providers/enrollment';
import {
  getActiveHealthBenefits,
  getAllCriticalIllness,
} from '../../../services/enrollment';
import { Profile } from '../../../types';
import { formatPrice } from '../../../utils/utils';

export type customCriticalIllnessModalProps = {
  showCriticalIllnessModal: boolean;
  handleCloseCriticalIllnessModal: () => void;
};

// todo: this data need to be extracted form criticalIllness.json file after its and GenerricTable comp refactor
const tableData = {
  headers: ['Plan', 'You', 'You & Spouse', 'You & Children', 'You & Family'],
  rows: [
    [
      '$10,000',
      formatPrice(1.92),
      formatPrice(3.21),
      formatPrice(5.63),
      formatPrice(7.04),
    ],
    [
      '$15,000',
      formatPrice(5.1),
      formatPrice(7.34),
      formatPrice(8.91),
      formatPrice(11.07),
    ],
    [
      '$20,000',
      formatPrice(6.29),
      formatPrice(8.11),
      formatPrice(11.37),
      formatPrice(13.16),
    ],
  ]
};

const CriticalIllnessModal = ({
  showCriticalIllnessModal,
  handleCloseCriticalIllnessModal,
}: customCriticalIllnessModalProps) => {
  const dataProfiles = getActiveHealthBenefits();
  const criticalIllnessPlan = dataProfiles.filter(
    (plan: any) => plan.type === 'critical-illness'
  );

  const [showDetails, setShowDetails] = React.useState<boolean>(false);
  const [showTableData, setShowTableData] = React.useState<boolean>(false);
  const [criticalIllnessData, setCriticalIllnessData] = React.useState<any>({});
  const [radioButtonValue, setRadioButtonValue] = React.useState<
    string | number
  >(9);
  const [tellMeMore, setTellMeMore] = useState(false);
  const {
    activeHealthBenefits,
    updateCost,
    updatePlanName,
    criticalIlnessSelectedProfiles,
    updateSelectedProfiles,
  } = useContext(EnrollmentStore);

  const selectedTableIndex = useMemo(() => {
    const profilesRelationships = criticalIlnessSelectedProfiles?.map(
      (profiles: any) => {
        if (Array.isArray(profiles)) {
          return profiles[0].relationship.toLowerCase();
        }
        return profiles.relationship.toLowerCase();
      }
    );

    const uniqueRelationships = new Set(profilesRelationships);
    const sortedRelationships = Array.from(uniqueRelationships).sort(
      (r1: any, r2: any) => (r1 < r2 ? -1 : 1)
    );
    const concatenatedRelationships = sortedRelationships.join('-');

    switch (concatenatedRelationships) {
      case 'self':
        return 1;
      case 'self-spouse':
        return 2;
      case 'child-self':
        return 3;
      case 'child-self-spouse':
        return 4;
      default:
        return -1;
    }
  }, [criticalIlnessSelectedProfiles]);

  const dataPlans = useMemo(() => {
    return getAllCriticalIllness();
  }, []);

  useEffect(() => {
    if (dataPlans) {
      setCriticalIllnessData(dataPlans);
    }
  }, [dataPlans]);

  useEffect(() => {
    if (selectedTableIndex > -1) {
      const newPlansPrices = dataPlans['plans'].map((plan: any) => {
        if (plan.id.includes('cil-10000')) {
          plan.cost = +tableData['rows'][0][selectedTableIndex].replace(
            '$',
            ''
          );
        } else if (plan.id.includes('cil-15000')) {
          plan.cost = +tableData['rows'][1][selectedTableIndex].replace('$', '');
        } else if (plan.id.includes('cil-20000')) {
          plan.cost = +tableData['rows'][2][selectedTableIndex].replace(
            '$',
            ''
          );
        }

        return plan;
      });
      setCriticalIllnessData({
        ...dataPlans,
        plans: newPlansPrices,
      });
    }
  }, [dataPlans, selectedTableIndex]);

  useEffect(() => {
    const currentBenefit = activeHealthBenefits.find(
      (benefit: any) => benefit.type === 'critical-illness'
    );
    const index = criticalIllnessData.plans?.findIndex(
      (plan: any) => plan.id === currentBenefit?.plan?.id
    );
    setRadioButtonValue(index);
  }, [activeHealthBenefits, criticalIllnessData]);

  const handleRadioButtonValue = (e: string | number) => {
    setRadioButtonValue(e);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const toggleTableData = () => {
    setShowTableData(!showTableData);
  };

  const handleTellMeMore = () => {
    setTellMeMore((prev) => !prev);
  };

  if (!criticalIllnessPlan.length) {
    return null;
  }

  const planProfiles = criticalIllnessPlan[0]['profiles'];
  const childProfiles = planProfiles.filter(
    (profile: Profile) => profile.relationship === 'child'
  );
  const nonChildProfiles: [] = planProfiles.filter(
    (profile: Profile) => profile.relationship !== 'child'
  );
  const profilesItems =
    childProfiles.length > 1 ? nonChildProfiles : planProfiles;

  return (
    <>
      {criticalIllnessData ? (
        <Modal
          show={showCriticalIllnessModal}
          onHide={handleCloseCriticalIllnessModal}
          dialogClassName="modal-xl"
          className="custom-health-savings-modal"
        >
          <ModalHeader
            leftIcon={light('face-thermometer')}
            leftIconVariant="icon-default"
            rightButton={
              <div onClick={toggleDetails} className={'icon-button-wrapper'}>
                <IconButton
                  variant="icon-default"
                  icon={light('circle-exclamation')}
                  extLabel="Benefit Info"
                />
              </div>
            }
          >
            <h4 className="text-sm-20-lg-32">
              {criticalIllnessData['generic']
                ? criticalIllnessData['generic']['title']
                : ''}
            </h4>
          </ModalHeader>
          <ModalBody className="pt-4">
            <Row className={`expandable-wrapper ${showDetails ? 'open' : ''}`}>
              <div className="d-flex justify-content-center">
                <DefaultCard
                  closeButton={
                    <Link 
                      className='pt-0' 
                      isAlignAtEnd 
                      isWhiteIcon 
                      rightIcon={light('close')}
                      onClick={toggleDetails}
                    />                      
                  }
                  variant="blue-motif"
                  leftHeading={
                    criticalIllnessData['generic']
                      ? criticalIllnessData['generic']['info']['title']
                      : ''
                  }
                  leftSubheading={
                    <Container className={'px-0 d-flex flex-column'}>
                      {criticalIllnessData['generic'] ? (
                        <>
                          <span>
                            {
                              criticalIllnessData['generic']['info'][
                                'description'
                              ]
                            }
                          </span>
                          {tellMeMore && (
                            <span className={'mt-1'}>
                              {
                                criticalIllnessData['generic']['info'][
                                  'additionalDescription'
                                ]
                              }
                            </span>
                          )}
                        </>
                      ) : (
                        ''
                      )}
                    </Container>
                  }
                  button={
                    <Button
                      onClick={handleTellMeMore}
                      variant="primary-shadow"
                      size="sm"
                    >
                      {tellMeMore ? 'Tell me less' : 'Tell me more'}
                    </Button>
                  }
                  className={'mb-12'}
                />
              </div>
            </Row>
            <div className="pb-10 pb-lg-11">
              <h5 className={'h5 mb-0'}>Who would you like to cover?</h5>
            </div>
            <div className="pb-7 pb-lg-12 d-lg-flex">
              {profilesItems.map((profile: Profile, index: number) => (
                <UserCard
                  item={profile}
                  key={index}
                  selected={criticalIlnessSelectedProfiles?.find(
                    (e: any) => e.id === profile.id
                  )}
                  className={'mb-7 mb-lg-0 me-lg-7'}
                  onClick={() =>
                    updateSelectedProfiles('critical-ilness', profile)
                  }
                />
              ))}
              {childProfiles.length > 1 && (
                <UserCardGrouped
                  avatars={childProfiles}
                  value={'children'}
                  selected={criticalIlnessSelectedProfiles?.find((e: any) =>
                    Array.isArray(e)
                  )}
                  className={'mb-7 mb-lg-11 me-lg-7'}
                  onClick={() =>
                    updateSelectedProfiles('critical-ilness', childProfiles)
                  }
                />
              )}
            </div>
            {!showTableData && (
              <Fragment>
                <div className="d-flex justify-content-between align-items-center pb-7 pb-lg-11">
                  <h5 className={'h5 mb-0'}>Which plan would you like?</h5>
                  <Link isBlue onClick={toggleTableData}>
                    View cost across plans
                  </Link>
                </div>
                <Row>
                  <RadioGroup
                    name={'pricing-test1'}
                    value={radioButtonValue}
                    onChange={handleRadioButtonValue}
                  >
                    <div>
                      {criticalIllnessData['plans']
                        ? criticalIllnessData['plans'].map(
                            (
                              plan: {
                                title: string;
                                cost: any;
                                coverage: number;
                              },
                              i: any
                            ) => (
                              <SelectableCard
                                title={plan.title}
                                prePaydPeriod={plan.cost}
                                radioButtonValue={i}
                                selected={radioButtonValue === i}
                                disabled={
                                  i === criticalIllnessData['plans'].length - 1
                                }
                                key={i}
                              />
                            )
                          )
                        : ''}
                    </div>
                  </RadioGroup>
                </Row>
              </Fragment>
            )}
            {showTableData && (
              <Row className={'mt-7 mt-lg-11'}>
                <div className={'d-flex align-items-center'}>
                  <FontAwesomeIcon icon={light('arrow-left')} />
                  <h5 className={'h5 mb-0 ms-4'}>Plan coverage level costs</h5>
                </div>
                <Col className={'mt-10 overflow-auto'}>
                  <GenericTable
                    selectedColumnIndex={selectedTableIndex}
                    tableData={tableData}
                  />
                </Col>
                <div className={'mt-10'}>
                  <Button
                    variant="primary"
                    leftIcon={light('arrow-left')}
                    onClick={toggleTableData}
                  >
                    Back to Plan Options
                  </Button>
                </div>
              </Row>
            )}
          </ModalBody>
          <Modal.Footer className="pt-7">
            <Button
              onMouseDown={handleCloseCriticalIllnessModal}
              className="text-nowrap mt-7 mt-lg-0"
              variant="primary-outline"
            >
              Cancel
            </Button>
            <Button
              onMouseDown={() => {
                updateCost(
                  'critical-illness',
                  criticalIllnessData.plans[radioButtonValue]
                );
                updatePlanName(
                  'critical-illness',
                  criticalIllnessData.plans[radioButtonValue]
                );
                handleCloseCriticalIllnessModal();
              }}
              className="text-nowrap"
              variant="primary"
            >
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        'NO DATA'
      )}
    </>
  );
};

export default CriticalIllnessModal;
